import React from 'react';
import PropTypes from 'prop-types';

import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { styleUtils, Tooltip, IconButton } from '@ublend-npm/aulaui-next';

import formatDate from '../../../../../../core/utils/formatDate';
import { HoverIconButton } from './MaterialListItem.styled';
import {
  MATERIAL_UNHIDE_TOOLTIP,
  MATERIAL_UNHIDE_FOLDER_TOOLTIP,
  MATERIAL_HIDE,
  MATERIAL_HIDE_FOLDER,
} from '../../../../../constants/texts';
import { ENTER } from '../../../../../utils/keyCodes';
import { evaluateMaterialAction } from '@core/materials/permissions';

const getLabel = (isFolder, hidden, scheduledFor) => {
  if (hidden && scheduledFor) {
    return `Scheduled for ${formatDate(scheduledFor)}. Click to change.`;
  }
  if (isFolder) {
    return hidden ? MATERIAL_UNHIDE_FOLDER_TOOLTIP : MATERIAL_HIDE_FOLDER;
  }
  return hidden ? MATERIAL_UNHIDE_TOOLTIP : MATERIAL_HIDE;
};

const getIcon = (hidden, scheduledFor) => {
  if (hidden && scheduledFor) {
    return ScheduleIcon;
  }

  return hidden ? VisibilityOffIcon : VisibilityIcon;
};

const getAriaMessage = (isFolder, hidden, scheduledFor) => {
  const object = isFolder ? 'folder' : 'page';
  if (hidden && scheduledFor) {
    return `The ${object} is currently scheduled to be published on ${formatDate(
      scheduledFor
    )}.`;
  }
  return `The ${object} is currently ${hidden ? 'hidden' : 'visible'}.`;
};

const MaterialVisibilityToggle = ({
  isFolder,
  hidden,
  scheduledFor,
  onToggleVisibility,
  openScheduleDialog,
  canEdit = true,
  permissions = [],
  isRbacEnabled = false,
}) => {
  const label = getLabel(isFolder, hidden, scheduledFor);

  // If RBAC is disabled, pass empty permissions to force fallback usage
  const effectivePermissions = !isRbacEnabled ? [] : permissions;

  const toggleStatus = evaluateMaterialAction('TOGGLE_VISIBILITY', {
    permissions: effectivePermissions,
    fallback: canEdit,
  });
  const handleToggleClick = (e) => {
    e.stopPropagation();
    if (!toggleStatus.allowed) {
      return;
    }
    if (hidden && scheduledFor) {
      openScheduleDialog();
    } else {
      onToggleVisibility();
    }
  };

  const Button = hidden ? IconButton : HoverIconButton;

  return (
    <Tooltip
      title={toggleStatus.allowed ? label : toggleStatus.message}
      position="top"
    >
      <div>
        <Button
          icon={getIcon(hidden, scheduledFor)}
          size="xSmall"
          type="secondary"
          label={label}
          buttonAttributes={{
            tabIndex: 0,
            'aria-disabled': !toggleStatus.allowed,
          }}
          onClick={handleToggleClick}
          onKeyDown={(e) => e.keyCode === ENTER && handleToggleClick()}
          disabled={!toggleStatus.allowed}
        />
        <p css={[styleUtils.visuallyHidden()]} aria-live="polite">
          {getAriaMessage(isFolder, hidden, scheduledFor)}
        </p>
      </div>
    </Tooltip>
  );
};

MaterialVisibilityToggle.propTypes = {
  isFolder: PropTypes.bool,
  hidden: PropTypes.bool.isRequired,
  scheduledFor: PropTypes.string,
  onToggleVisibility: PropTypes.func.isRequired,
  openScheduleDialog: PropTypes.func.isRequired,
  canEdit: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string),
  isRbacEnabled: PropTypes.bool,
};

MaterialVisibilityToggle.defaultProps = {
  isFolder: false,
  scheduledFor: null,
  canEdit: true,
  permissions: [],
};

export default React.memo(MaterialVisibilityToggle);
